<script lang="ts" setup>

import { computed, watch, onMounted, onBeforeUnmount } from "vue";
import { FilterSpecification, GeoJSONSource } from "maplibre-gl";
import { addLayer, injectMap, removeLayersAndSources } from '../../../utils/MapUtils';
import { addIcon, getArrow, getCircle, getEmpty } from '../../../utils/Bitmaps';
import { tweenOpacity } from "../../../utils/Tweener";
import { type TFeatureCollection } from '@/shared/types/geojson';
import { type ProgressMode } from "@/shared/types/types";
import { firstProgress } from "@/utils/markerProgress";

const $map = injectMap();

const props = defineProps<{
	kmFeatures: TFeatureCollection;
	arrowFeatures: TFeatureCollection;
	progress: number;
	progressMode?: ProgressMode;
	editMode?: boolean;
}>();

const kmVisibility = computed((): number => {
	if (props.editMode) return 1;
	if (props.progressMode == "animation" || props.progressMode == "animationVideo") return props.progress;
	return 1;
});

function getSource(name: string): GeoJSONSource {
	return $map.getSource(name) as GeoJSONSource;
}

function initMap() {

	addIcon("km-bg", getCircle(), $map);
	addIcon("racemaps-arrow", getArrow(), $map);
	addIcon("empty", getEmpty(), $map);

	const filter: FilterSpecification = ['>=', ['zoom'], ["get", "zoom"]];

	// Kilometer markers
	addLayer($map, {
		id: "map-symbols-km",
		type: 'symbol',
		source: {
			type: 'geojson',
			data: props.kmFeatures,
			promoteId: "text",
		},
		layout: {
			'icon-image': 'km-bg',
			"icon-ignore-placement": true,
			"icon-allow-overlap": true,

			"text-field": ["get", "text"],
			"text-ignore-placement": true,
			"text-allow-overlap": true,
			/* Variable text size based on number of chars */
			"text-size": ["step", ["length", ["get", "text"]], 8,
				1, 10,
				2, 9,
				3, 8
			],
			"text-font": [
				"Open Sans Regular",
			],
		},
		paint: {
			"text-opacity": ["number", ["feature-state", "opacity"], 0],
			"icon-opacity": ["number", ["feature-state", "opacity"], 0]
		},
		filter,
	}, false);

	addLayer($map, {
		id: "map-symbols-arrows",
		type: 'symbol',
		source: {
			type: 'geojson',
			data: props.arrowFeatures
		},
		layout: {
			'icon-image': 'racemaps-arrow',
			"icon-ignore-placement": true,
			"icon-allow-overlap": true,
			"icon-rotate": ["get", "angle"],
			"icon-offset": ["match", ["get", "position"], "left", ["literal", [-10, 0]], ["literal", [10, 0]]]
		},
		paint: {
		},
		filter,
	}, false);

	setKmOpacityFromProgress(kmVisibility.value);
}

function setKmOpacityFromProgress(newP: number, oldP?: number) {
	for (const feature of props.kmFeatures.features) {
		const id = feature.properties!.text;
		const p = firstProgress(feature.properties!.__progress, 0);
		const oldV = oldP === undefined ? -1 : oldP > p;
		const newV = newP > p;
		if (oldV != newV) {
			tweenOpacity(id, newV ? 1 : 0, "map-symbols-km", true, $map);
		}
	}
}

watch(() => props.kmFeatures, () => {
	getSource("map-symbols-km").setData(props.kmFeatures);
	setKmOpacityFromProgress(kmVisibility.value);
});

watch(() => props.arrowFeatures, () => {
	getSource("map-symbols-arrows").setData(props.arrowFeatures);
});

watch(kmVisibility, (newP: number, oldP: number) => {
	setKmOpacityFromProgress(newP, oldP);
});

onMounted(() => {
	initMap();
});

onBeforeUnmount(() => {
	removeLayersAndSources($map, [
		"map-symbols-km",
		"map-symbols-arrows"
	]);
});

</script>

<template></template>

<style></style>